import { apiResponse } from "@/app/types";
import { getAuthorizationHeader } from "@/lib/utils";
import axios, {
	AxiosInstance,
	AxiosRequestConfig,
	AxiosResponse,
	isAxiosError,
} from "axios";

interface APIServiceConfig {
	url: string;
}

class APIService {
	private instance: AxiosInstance;

	constructor(config: APIServiceConfig) {
		this.instance = axios.create({
			baseURL: config.url,
			timeout: 30000,
			timeoutErrorMessage: "Time out!",
		});
	}

	async getExternal(path: string, config?: AxiosRequestConfig): Promise<any> {
		try {
			const response: AxiosResponse = await this.instance.get(path, {
				...config,
			});
			return response.status && response.data;
		} catch (error) {
			console.error(error);
			if (isAxiosError(error)) {
				throw new Error(error.message || "External request failed.");
			}
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async get(path: string, config?: AxiosRequestConfig): Promise<any> {
		try {
			const response: AxiosResponse = await this.instance.get(path, {
				headers: getAuthorizationHeader(),
				...config,
			});
			return response.data.status ? response.data.data : null;
		} catch (error) {
			console.error(error);
			if (isAxiosError(error) && error.response?.data) {
				const data = error.response.data as { message?: string };
				const msg =
					typeof data?.message === "string" ? data.message : error.message;
				throw new Error(msg);
			}
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async post(
		path: string, // token
		data: any, // {token}
		config?: AxiosRequestConfig
	): Promise<apiResponse> {
		try {
			const response: AxiosResponse = await this.instance.post(path, data, {
				headers: getAuthorizationHeader(),
				...config,
			});
			const body = response.data as apiResponse;
			if (body && body.status === false) {
				const msg =
					typeof body.message === "string" && body.message.length > 0
						? body.message
						: "Request failed.";
				throw new Error(msg);
			}
			return body;
		} catch (error) {
			console.error(error);
			if (isAxiosError(error)) {
				if (error.code === "ECONNABORTED") {
					throw new Error("Request timed out. Please try again.");
				}
				const data = error.response?.data as { message?: string } | undefined;
				const fromBody =
					typeof data?.message === "string" && data.message.length > 0
						? data.message
						: null;
				if (fromBody) {
					throw new Error(fromBody);
				}
				if (error.response?.status) {
					throw new Error(
						`Request failed (${error.response.status}). Please try again.`
					);
				}
				throw new Error(
					error.message || "Network error. Check your connection and try again."
				);
			}
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async put(
		path: string,
		data: any,
		config?: AxiosRequestConfig
	): Promise<any> {
		try {
			const response: AxiosResponse = await this.instance.put(path, data, {
				headers: getAuthorizationHeader(),
				...config,
			});
			return response.data;
		} catch (error) {
			console.error(error);
			if (isAxiosError(error)) {
				const data = error.response?.data as { message?: string } | undefined;
				const msg =
					typeof data?.message === "string" && data.message.length > 0
						? data.message
						: error.message || "Request failed.";
				throw new Error(msg);
			}
			throw error instanceof Error ? error : new Error(String(error));
		}
	}

	async delete(path: string, config?: AxiosRequestConfig): Promise<any> {
		try {
			const response: AxiosResponse = await this.instance.delete(path, {
				headers: getAuthorizationHeader(),
				...config,
			});
			return response.data;
		} catch (error) {
			console.error(error);
			if (isAxiosError(error)) {
				const data = error.response?.data as { message?: string } | undefined;
				const msg =
					typeof data?.message === "string" && data.message.length > 0
						? data.message
						: error.message || "Delete request failed.";
				throw new Error(msg);
			}
			throw error instanceof Error ? error : new Error(String(error));
		}
	}
}

export default APIService;

import { getApiUrl } from "@/lib/rpc-url";
export const apiService = new APIService({ url: getApiUrl() });
